import { default as React } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
    createColumnHelper,
} from "@tanstack/react-table";

function EntityPageContactList({
    data,
    isAuthenticated,
    controlType,
    saveRecord,
    deleteRecord,
    refreshDataSet,
    setUi,
    user,
    updateTableState,
    entityid,
    salutationOptions,
    positionOptions,
}) {

    const dataSet = 'contacts';

    // --- TanStack Table Setup ---
    const tableData = data || [];
    const columnHelper = createColumnHelper();
    const createId = createElementIdFactory("entity-contact-list");
    const tableElementIds = createReusableTableElementIds(createId, "contacts");
    const latestSaveTokenByRowRef = React.useRef(new Map());

    const normalizeDropdownOptions = (options) => {
        if (!Array.isArray(options)) {
            return [];
        }

        const isPlaceholderOption = (value, label) => {
            const normalizedValue = String(value || "").trim().toLowerCase();
            const normalizedLabel = String(label || "").trim().toLowerCase();

            const isSelectPlaceholder = (text) => /^select\s*(?:\.\.\.|…)?$/.test(text);

            return isSelectPlaceholder(normalizedValue) || isSelectPlaceholder(normalizedLabel);
        };

        return options
            .map((opt) => {
                if (typeof opt === "string") {
                    const value = String(opt);
                    const label = String(opt);
                    if (isPlaceholderOption(value, label)) {
                        return null;
                    }
                    return { value, label };
                }

                if (!opt || typeof opt !== "object") {
                    return null;
                }

                const value =
                    opt.value ??
                    opt.label ??
                    opt.salutation ??
                    opt.title ??
                    opt.position ??
                    opt.name ??
                    opt.code ??
                    opt.description ??
                    "";
                const label =
                    opt.label ??
                    opt.value ??
                    opt.salutation ??
                    opt.title ??
                    opt.position ??
                    opt.name ??
                    opt.description ??
                    opt.code ??
                    "";

                if (!value && !label) {
                    return null;
                }

                if (isPlaceholderOption(value, label)) {
                    return null;
                }

                return {
                    ...opt,
                    value: String(value),
                    label: String(label),
                };
            })
            .filter(Boolean);
    };

    const salutationDropdownOptions = normalizeDropdownOptions(salutationOptions);
    const positionDropdownOptions = normalizeDropdownOptions(positionOptions);

    const showValidationError = (message) => {
        if (typeof setUi === "function") {
            setUi(prev => ({ ...prev, errorMessage: message, showAlert: true }));
        }
    };

    const isValidEmailFormat = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

    const validateContactRecord = (record) => {
        const firstName = String(record?.first_name || "").trim();
        const email = String(record?.email || "").trim();

        if (!firstName) {
            return "Enter a firstname or lastname.";
        }

        if (email && !isValidEmailFormat(email)) {
            return "The email is not a correct format";
        }

        return "";
    };

    const columns = [
        columnHelper.accessor("entity_contact_id", {
            id: "entity_contact_id",
            header: "ID",
            cell: ({ getValue }) => {
                const id = getValue();
                if (String(id || "").startsWith("temp-")) {
                    return "";
                }
                return id;
            },
            size: 60,
        }),
        columnHelper.accessor("title", {
            id: "title",
            header: "Title",
            cell: EditableCell,
            size: 175,
            meta: {
                options: salutationDropdownOptions,
                blankOptionLabel: "",
                useClearableSingleSelect: true,
                saveOnBlurOnly: true,
            },
        }),
        columnHelper.accessor("first_name", {
            id: "first_name",
            header: "First Name",
            cell: EditableCell,
            size: 185,
        }),
        columnHelper.accessor("middle_initial", {
            id: "middle_initial",
            header: "Middle Initial",
            cell: EditableCell,
            size: 85,
        }),
        columnHelper.accessor("surname", {
            id: "surname",
            header: "Surname",
            cell: EditableCell,
            size: 185,
        }),
        columnHelper.accessor("position", {
            id: "position",
            header: "Position",
            cell: EditableCell,
            size: 185,
            meta: {
                options: positionDropdownOptions,
                blankOptionLabel: "",
                useClearableSingleSelect: true,
                saveOnBlurOnly: true,
            },
        }),
        columnHelper.accessor("phone", {
            id: "phone",
            header: "Phone",
            cell: EditableCell,
            size: 120,
        }),
        columnHelper.accessor("email", {
            id: "email",
            header: "Email",
            cell: EditableCell,
            size: 200,
        }),
        columnHelper.accessor("address_extra", {
            id: "address_extra",
            header: "Address Extra",
            cell: EditableCell,
            size: 185,
        }),
        columnHelper.display({
            id: "actions",
            header: "",
            cell: (e) => (
                <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={() => handleDelete({ row: e.row })}
                >
                    Delete
                </Button>
            ),
            size: 90,
            enableSorting: false,
        }),
    ];

    async function handleDelete({ row }) {
        const shouldDelete = window.confirm("Are you sure you want to delete this record?");
        if (!shouldDelete) {
            return;
        }

        try {
            await deleteRecord(dataSet, row.original);
            if (typeof refreshDataSet === "function") {
                await refreshDataSet(dataSet);
            }
        } catch (error) {
            console.error("Delete failed:", error);
        }
    }

    async function addRecord(event) {
        let localdata = [...data];
        const temporaryContactId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        localdata.unshift({
            entity_id: entityid,
            entity_contact_id: temporaryContactId,
            client_row_key: temporaryContactId,
            created_by: user,
            email: "",
            phone: "",
            position: "",
            title: "",
            first_name: "",
            middle_initial: "",
            surname: "",
            address_extra: "",
            last_edited_by: user,
            new_record: true
        });
        updateTableState(localdata, dataSet);
    }

    // --- Render Functions ---
    function renderComponent() {
        return (
            <div className="entityContacts">
                <Row >
                    <Col className="text-right" style={{ marginBottom: '12px' }} >
                        <Button variant="secondary" size="sm" onClick={addRecord}  >Add</Button>
                    </Col>
                </Row>

                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {data
                            ? (
                                <GenericTable
                                    section="entity-contacts"
                                    columns={columns}
                                    data={tableData}
                                    pageSize={PAGE_SIZES.entityDetailContactsTab}
                                    elementIds={tableElementIds}
                                    columnResizeMode="onChange"
                                    meta={{
                                        updateData: (rowIndex, columnId, value) => {
                                            updateTableState(rowIndex, columnId, value);
                                        },
                                        handleSave: ({ row, column, value }) => {
                                            const liveRow = Array.isArray(data) ? data[row.index] : null;
                                            const record = {
                                                ...(liveRow || row.original || {}),
                                            };

                                            if (column?.id) {
                                                record[column.id] = value;
                                            }

                                            const validationError = validateContactRecord(record);
                                            if (validationError) {
                                                showValidationError(validationError);
                                                return;
                                            }

                                            const rowKey = String(record.entity_contact_id || record.client_row_key || `row-${row.index}`);
                                            const previousToken = latestSaveTokenByRowRef.current.get(rowKey) || 0;
                                            const nextToken = previousToken + 1;
                                            latestSaveTokenByRowRef.current.set(rowKey, nextToken);

                                            saveRecord(dataSet, record)
                                                .then(() => {
                                                    const latestToken = latestSaveTokenByRowRef.current.get(rowKey);
                                                    if (latestToken === nextToken) {
                                                        // Keep focus on the same row while editing by avoiding
                                                        // full table refresh after each field save.
                                                    }
                                                })
                                                .catch((err) => {
                                                    console.error("Save failed:", err);
                                                });
                                        },
                                        handleDelete,
                                    }}
                                />
                            )
                            : null}
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div className="EntityPageContactList">
            {isAuthenticated ? renderComponent() : null}
        </div>
    );
}

export default EntityPageContactList;
